'use client';

import { useEffect, useMemo, useRef } from 'react';

import { useQuery } from '@apollo/client/react';
import { Paper, Stack, Box } from '@mui/material';
import { Ads, normalizeSlices, SliceContainer } from '@scorenco/components';
import {
  ssrApolloService,
  useRouteInfo,
  useTranslation,
  Types,
  useLiveNewHit,
} from '@scorenco/core';
import {
  NextAdArea,
  NextAkceloConfig,
  NextGooglePublisherTagConfig,
  NextOptiDigitalAdArea,
  NextOptiDigitalConfig,
  NextPageWithLayout,
  ssrIsAuthenticated,
} from '@scorenco/next';
import Head from 'next/head';

import { PageLayout, SportChip } from '@/components';
import { useAppRedirect } from '@/hooks/useAppRedirect';
import { WebLayout } from '@/layouts';
import { RouteKey } from '@/routes';
import { getSport } from '@/utils';

import {
  GetEventDocument,
  GetLivePageDocument,
  GetEventWithSameTeamsDocument,
} from './_internal';
import { EventCompetitionName } from './_internal/components/EventCompetitionName';
import { EventDate } from './_internal/components/EventDate';
import { EventMetadata } from './_internal/components/EventMetadata';
import { EventScore } from './_internal/components/EventScore';
import { EventTeam } from './_internal/components/EventTeam';
import { EventTitle } from './_internal/components/EventTitle';
import { GetTournamentIdDocument } from './_internal/Event.query.generated';
import { getEventTeams } from './_internal/utils/eventGuards';
import { eventQueryParams } from './_internal/utils/eventQueryParams';

const Event: NextPageWithLayout<{
  preview?: boolean;
  sport: Types.Clubs_Sport;
}> = ({ preview, sport }) => {
  const { locale } = useTranslation();
  useAppRedirect();

  const { query } = useRouteInfo();
  const urlInfo = eventQueryParams(query);

  const eventWithSameTeamsQuery = useQuery(GetEventWithSameTeamsDocument, {
    variables: { id: urlInfo.eventWithSameTeamsId },
    skip: !!urlInfo.forceEventId,
  });
  const eventId =
    urlInfo.forceEventId ||
    eventWithSameTeamsQuery.data?.competitions_event_with_same_teams_by_pk
      ?.events?.[0]?.id;

  const eventQuery = useQuery(GetEventDocument, {
    variables: { id: eventId as number },
    skip: !eventId,
  });
  const event = eventQuery.data?.competitions_event_detail_by_id?.[0];
  const eventTeams = useMemo(() => getEventTeams(event?.teams), [event?.teams]);

  // Sélection déterministe d'une équipe pour les publicités (basée sur l'ID de l'événement)
  const randomTeamName = useMemo(() => {
    if (eventTeams.length === 0) return undefined;
    // Utilise l'ID de l'événement pour déterminer quelle équipe afficher
    const teamIndex = (event?.id ?? 0) % Math.min(2, eventTeams.length);
    return eventTeams[teamIndex]?.name_in_competition;
  }, [eventTeams, event?.id]);

  const getTournamentQuery = useQuery(GetTournamentIdDocument, {
    variables: { eventId: event?.id as number },
    skip: !event?.id || event?.family !== 'tournament',
    context: {
      role: 'anonymous',
    },
  });

  const tournamentUrl = getTournamentQuery.data?.tournaments_game?.[0]
    ?.home_engagement?.phase?.rule?.challenge?.tournament_id
    ? process.env.NEXT_PUBLIC_TOURNAMENT_URL +
      '/' +
      getTournamentQuery.data?.tournaments_game?.[0]?.home_engagement?.phase
        ?.rule?.challenge?.tournament_id
    : undefined;

  const livePageQuery = useQuery(GetLivePageDocument, {
    variables: {
      locale: locale.split('-')[0],
      slug: urlInfo.sport,
      status: preview
        ? Types.PublicationStatus.Draft
        : Types.PublicationStatus.Published,
    },
    context: {
      clientName: 'strapi',
    },
  });

  const slices = normalizeSlices(livePageQuery?.data?.pagesLives?.[0]?.body);

  const newHitMutation = useLiveNewHit();
  const newHitExecuted = useRef(false);

  useEffect(() => {
    if (!newHitExecuted.current && eventId) {
      newHitMutation
        .execute({
          eventId: Number(eventId),
        })
        .catch((error) => {
          console.error("Erreur lors de l'enregistrement du hit:", error);
        });
      newHitExecuted.current = true;
    }
  }, [eventId, newHitMutation]);

  return (
    <>
      {sport?.image && (
        <Head>
          <link
            rel="preload"
            as="image"
            href={sport.image}
            fetchPriority="high"
          />
        </Head>
      )}
      <EventMetadata event={event as Types.Competitions_Event_Detail} />
      <NextOptiDigitalConfig adUnit="/157593316/od.scorenco.com/match" />
      <NextAkceloConfig />
      <NextGooglePublisherTagConfig />

      <PageLayout
        image={sport?.image}
        breadcrumbs={[
          {
            label: event?.sport?.name || '',
            href: {
              routeKey: RouteKey.Sport,
              params: { sport: event?.sport?.slug || urlInfo.sport },
            },
          },
          {
            label:
              eventTeams[0]?.name_in_competition +
              ' - ' +
              eventTeams[1]?.name_in_competition,
          },
        ]}
      >
        <Paper
          variant="bordered"
          sx={{
            paddingTop: { xs: 2, sm: 3, lg: 4 },
            paddingX: { xs: 2, sm: 3, lg: 4 },
            borderRadius: '0 0 0.5rem 0.5rem',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            paddingBottom: 0,
          }}
        >
          <Stack width="100%" alignItems="center" gap={2}>
            <SportChip sport={urlInfo.sport} />

            <EventCompetitionName
              event={event as Types.Competitions_Event_Detail}
              tournamentUrl={tournamentUrl}
            />

            <EventTitle
              event={event as Types.Competitions_Event_Detail}
              width="100%"
              tournamentUrl={tournamentUrl}
            />

            <Stack flexDirection="row" width="100%" alignItems="center">
              <EventTeam
                event={event as Types.Competitions_Event_Detail}
                tournamentUrl={tournamentUrl}
              />
              <EventScore event={event as Types.Competitions_Event_Detail} />
              <EventScore
                event={event as Types.Competitions_Event_Detail}
                isAway
              />
              <EventTeam
                event={event as Types.Competitions_Event_Detail}
                isAway
                tournamentUrl={tournamentUrl}
              />
            </Stack>

            <EventDate event={event as Types.Competitions_Event_Detail} />
          </Stack>
          {(event?.event?.live_hit_counts?.hit_count ?? 0) > 0 && (
            <Box
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                width: '100%',
                textAlign: 'center',
                fontSize: '0.75rem',
                borderRadius: '0.5rem 0.5rem 0 0',
              }}
            >
              Ce live a été consulté{' '}
              {event?.event?.live_hit_counts?.hit_count || 0} fois
            </Box>
          )}
        </Paper>
        {sport?.id && (
          <Ads
            type="event"
            sport_id={sport?.id}
            style={{ marginTop: '1rem' }}
            department_id={event?.place?.city?.department?.id}
            club_name={randomTeamName}
          />
        )}

        <NextAdArea
          akceloSlotId="Scorenco_PlayRVideo"
          akceloAdUnitClass="akcelo-adunit-15786"
          withGooglePublisherTag
        />
      </PageLayout>

      {/* TODO: Comment this when we have a campaign */}
      <NextOptiDigitalAdArea areaId="Billboard_1" />
      <NextOptiDigitalAdArea areaId="Mobile_Pos1" />

      <SliceContainer slices={slices} />

      <NextOptiDigitalAdArea areaId="Billboard_Bottom" />
      <NextOptiDigitalAdArea areaId="Mobile_Bottom" />
    </>
  );
};

export const getServerSideProps = WebLayout.buildGetServerSideProps({}, [
  async ({ query, req, res }, previous) => {
    const { apolloClient, addApolloState } = ssrApolloService();

    if (query['no-ssr'] === undefined) {
      let urlInfo;

      try {
        urlInfo = eventQueryParams(query);
      } catch (_error) {
        return { notFound: true };
      }

      let event;
      if (urlInfo.forceEventId) {
        const eventQuery = await apolloClient.query({
          query: GetEventDocument,
          variables: { id: urlInfo.forceEventId },
        });
        event = eventQuery.data?.competitions_event_detail_by_id?.[0];

        if (!event && req.url) {
          return {
            redirect: {
              statusCode: 302,
              destination: req.url.split('?')[0],
            },
          };
        }
      }

      if (!event && urlInfo) {
        const eventWithSameTeamsQuery = await apolloClient.query({
          query: GetEventWithSameTeamsDocument,
          variables: { id: urlInfo.eventWithSameTeamsId as number },
        });
        const eventId =
          eventWithSameTeamsQuery.data?.competitions_event_with_same_teams_by_pk
            ?.events?.[0]?.id;

        if (eventId) {
          const eventQuery = await apolloClient.query({
            query: GetEventDocument,
            variables: { id: eventId },
          });
          event = eventQuery.data?.competitions_event_detail_by_id?.[0];
        }
      }

      if (!event) {
        return {
          notFound: true,
        };
      }

      const authenticated = await ssrIsAuthenticated({
        request: req,
        response: res,
      });
      if (authenticated) {
        return {
          redirect: {
            statusCode: 302,
            destination: `${process.env.NEXT_PUBLIC_MOBILE_URL}/events/${event.id}`,
          },
        };
      }

      const sport = await getSport(urlInfo.sport);

      return addApolloState({
        ...previous,
        props: {
          ...previous.props,
          sport,
        },
      });
    }
    return addApolloState(previous);
  },
]);

Event.getLayout = WebLayout.buildGetLayout({
  strapiMenu: true,
  amplify: { protectedPage: false },
});

export default Event;
